import { supabase } from './supabaseClient';
import { getNegocioId } from './negocioActual';

// Factura con CAI (SAR Honduras, sql/094). Modalidad autoimpresor por sistema
// computarizado. La factura queda congelada en la BD; se anula, no se borra.

export const IMPUESTOS = [
  { valor: 'g15', etiqueta: 'Gravado 15 % (lo normal)' },
  { valor: 'g18', etiqueta: 'Gravado 18 % (bebidas alcohólicas y cigarrillos)' },
  { valor: 'exento', etiqueta: 'Exento de ISV' },
];

const DIA = 86400000;

// CAI activo del negocio (o null).
export async function obtenerCaiActivo() {
  const { data, error } = await supabase.from('cai_autorizaciones').select('*')
    .eq('negocio_id', getNegocioId()).eq('tipo_doc', '01').eq('activo', true).maybeSingle();
  if (error) throw error;
  return data || null;
}

// Estado del CAI para avisos: vencido, agotado, por vencer (≤ 30 días) o por
// agotarse (≤ 10 % del rango).
export function estadoCai(cai) {
  if (!cai) return { puede: false, motivo: 'sin_cai' };
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const limite = new Date(`${cai.fecha_limite}T00:00:00`);
  const dias = Math.round((limite - hoy) / DIA);
  const total = cai.rango_fin - cai.rango_inicio + 1;
  const quedan = Math.max(cai.rango_fin - cai.siguiente + 1, 0);
  if (dias < 0) return { puede: false, motivo: 'vencido', dias, quedan, total };
  if (quedan === 0) return { puede: false, motivo: 'agotado', dias, quedan, total };
  return {
    puede: true, dias, quedan, total,
    aviso: dias <= 30 ? 'por_vencer' : (quedan <= Math.max(Math.ceil(total * 0.1), 1) ? 'por_agotarse' : null),
  };
}

export async function guardarCai({ cai, establecimiento, puntoEmision, rangoInicio, rangoFin, fechaLimite, siguiente }) {
  const { data, error } = await supabase.rpc('guardar_cai', {
    p_cai: cai, p_establecimiento: establecimiento, p_punto_emision: puntoEmision,
    p_rango_inicio: rangoInicio, p_rango_fin: rangoFin, p_fecha_limite: fechaLimite,
    p_siguiente: siguiente || null,
  });
  if (error) throw error;
  return data;
}

export async function facturaDeVenta(ticket) {
  const { data } = await supabase.from('facturas').select('*')
    .eq('ticket_id', ticket).eq('estado', 'emitida').maybeSingle();
  return data || null;
}

export async function emitirFactura(ticket, { nombre, rtn, ordenCompra, constancia, registroSag } = {}) {
  const { data, error } = await supabase.rpc('emitir_factura', {
    p_ticket: ticket,
    p_cliente_nombre: nombre || null,
    p_cliente_rtn: rtn || null,
    p_exo_orden_compra: ordenCompra || null,
    p_exo_constancia: constancia || null,
    p_exo_registro_sag: registroSag || null,
  });
  if (error) throw error;
  return data;
}

export async function anularFactura(id, motivo) {
  const { data, error } = await supabase.rpc('anular_factura', { p_factura: id, p_motivo: motivo });
  if (error) throw error;
  return data;
}

// Facturas de un mes ('YYYY-MM'), más recientes primero.
export async function listarFacturas(mes) {
  const [a, m] = mes.split('-').map(Number);
  const desde = new Date(a, m - 1, 1);
  const hasta = new Date(a, m, 1);
  const { data, error } = await supabase.from('facturas').select('*')
    .eq('negocio_id', getNegocioId())
    .gte('fecha_emision', desde.toISOString()).lt('fecha_emision', hasta.toISOString())
    .order('correlativo', { ascending: false });
  if (error) throw error;
  return data || [];
}

// Libro de ventas del mes en CSV (Excel) — sirve al contador y para el SAR
// (art. 53: el sistema debe generar archivos de texto de lo facturado).
export function libroVentasCsv(facturas) {
  const col = ['Fecha', 'Número', 'CAI', 'Cliente', 'RTN cliente', 'Estado', 'Importe exonerado', 'Importe exento',
    'Importe gravado 15%', 'Importe gravado 18%', 'ISV 15%', 'ISV 18%', 'Descuentos', 'Total'];
  const esc = (v) => {
    const t = String(v ?? '');
    return /[",;\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  const dinero = (n) => Number(n || 0).toFixed(2);
  const filas = [...facturas].sort((x, y) => x.correlativo - y.correlativo).map((f) => {
    const anulada = f.estado === 'anulada';
    const v = (n) => (anulada ? '0.00' : dinero(n));
    return [
      new Date(f.fecha_emision).toLocaleDateString('es-HN', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      f.numero, f.cai, f.cliente_nombre, f.cliente_rtn || '', anulada ? 'ANULADA' : 'Emitida',
      v(f.importe_exonerado), v(f.importe_exento), v(f.importe_gravado_15), v(f.importe_gravado_18),
      v(f.isv_15), v(f.isv_18), v(f.descuentos), v(f.total),
    ].map(esc).join(',');
  });
  return `﻿${col.join(',')}\n${filas.join('\n')}\n`;
}

export function descargarTexto(nombre, contenido, tipo = 'text/csv;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([contenido], { type: tipo }));
  const a = document.createElement('a');
  a.href = url; a.download = nombre;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
